import { accAPI } from "..";
import {
  CurrentViewExportReqBody,
  DeleteExportResponse,
  DownloadExportParams,
  DownloadExportResponse,
  ExportIdParam,
  ExportListResponse,
  ExportModuleIdParam,
  ExportNotifyResponse,
  ExportResponse,
  ExportSsiUplineResponse,
  FsiActionReportExportReqBody,
  FullMapExportReqBody,
  SaqExportReqBody,
  SsiUplineExportReqBody,
} from "./exportV2API.types";

export const TAG_EXPORT_LIST = "ExportListV2";
export const TAG_EXPORT_NOTIFY = "ExportNotifyV2";

const basedPath = "/api";

export const exportV2API = accAPI
  .enhanceEndpoints({
    addTagTypes: [TAG_EXPORT_LIST, TAG_EXPORT_NOTIFY],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      fullmapExportV2: build.mutation<ExportResponse, FullMapExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/fullmap`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          // invalidate the export list cache to trigger the getExportListV2 query refetch automatically
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      currentViewExportV2: build.mutation<ExportResponse, CurrentViewExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/currentview`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          // invalidate the export list cache to trigger the getExportListV2 query refetch automatically
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      getExportListV2: build.query<ExportListResponse, ExportModuleIdParam>({
        query: (params) => {
          const { moduleId } = params;
          const queryParams = new URLSearchParams({
            ModuleID: moduleId,
          });
          return `${basedPath}/?${queryParams}`;
        },
        providesTags: (_result, _error, arg) => {
          // set up the tag for other query/mutation to invalidate cache
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        // keepUnusedDataFor: 0,
        extraOptions: {
          pathKey: "export",
        },
      }),
      deleteExportV2: build.mutation<DeleteExportResponse, ExportIdParam>({
        query: ({ exportId }) => ({
          url: `${basedPath}/${exportId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, arg) => {
          return [{ type: TAG_EXPORT_LIST, id: arg.moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      getExportDownloadV2: build.query<DownloadExportResponse, DownloadExportParams>({
        query: (params) => {
          const { ExportID, FileFormat, DownloadFileName } = params;
          const queryParams = new URLSearchParams({
            ExportID,
            FileFormat,
            DownloadFileName,
          });
          return `${basedPath}/download?${queryParams}`;
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      getExportNotificationV2: build.query<ExportNotifyResponse, ExportModuleIdParam>({
        query: ({ moduleId }) => {
          const queryParams = new URLSearchParams({
            ModuleID: moduleId,
          });
          return `${basedPath}/notify?${queryParams}`;
        },
        providesTags: (_result, _error, arg) => {
          const { moduleId, affAbo } = arg;
          return [{ type: TAG_EXPORT_NOTIFY, id: affAbo ? `${moduleId}-${affAbo}` : moduleId }];
        },
        extraOptions: { pathKey: "export" },
      }),
      customerListExportV2: build.mutation<ExportResponse, FullMapExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/fullmap/customers`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          // invalidate the export list cache to trigger the getExportListV2 query refetch automatically
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      orderHistoryExportV2: build.mutation<ExportResponse, FullMapExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/fullmap/customers/orders`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          // invalidate the export list cache to trigger the getExportListV2 query refetch automatically
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      ssiUplineExportV2: build.mutation<ExportSsiUplineResponse, SsiUplineExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/ssiupline`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          // invalidate the export list cache to trigger the getExportListV2 query refetch automatically
          const { ModuleID } = arg;
          return [{ type: TAG_EXPORT_LIST, id: ModuleID }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      renewalsExportV2: build.mutation<CurrentViewExportReqBody, CurrentViewExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/currentview`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          // invalidate the export list cache to trigger the getExportListV2 query refetch automatically
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      saqExportV2: build.mutation<ExportResponse, SaqExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/saq`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
      fsiActionReportExportV2: build.mutation<ExportResponse, FsiActionReportExportReqBody>({
        query: (body) => ({
          url: `${basedPath}/fsiactionreport`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, arg) => {
          const { moduleId } = arg;
          return [{ type: TAG_EXPORT_LIST, id: moduleId }];
        },
        extraOptions: {
          pathKey: "export",
        },
      }),
    }),
  });

export const {
  useFullmapExportV2Mutation,
  useCurrentViewExportV2Mutation,
  useGetExportListV2Query,
  useLazyGetExportListV2Query,
  useDeleteExportV2Mutation,
  useLazyGetExportDownloadV2Query,
  useGetExportNotificationV2Query,
  useLazyGetExportNotificationV2Query,
  useCustomerListExportV2Mutation,
  useOrderHistoryExportV2Mutation,
  useSsiUplineExportV2Mutation,
  useRenewalsExportV2Mutation,
  useSaqExportV2Mutation,
  useFsiActionReportExportV2Mutation,
  util,
} = exportV2API;
